/**
 * EmailGate - Email Capture Component
 *
 * The "gate" before showing results. Captures email for Klaviyo.
 * Includes social proof/testimonials in the background.
 */

import React, { useState } from 'react';

interface EmailGateProps {
  userName: string;
  title: string;
  caption?: string | undefined;
  description: string;
  helperText?: string;
  buttonLabel?: string;
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
}

const EmailGate: React.FC<EmailGateProps> = ({
  userName: _userName,
  title,
  caption,
  description,
  helperText,
  buttonLabel,
  email,
  onEmailChange,
  onSubmit,
  onBack,
  isSubmitting,
  error
}) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = isValidEmail && agreedToTerms && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) {
      onSubmit();
    }
  };

  return (
    <div className="email-gate">


      {/* Email Modal */}
      <div className="email-gate-modal">
        {caption && <span className='caption'>{caption}</span>}
        {/* Background Testimonials (blurred) */}
        <div className="email-gate-testimonials">
          <div className="testimonial-card">
            <p>"My Meyer Lemon is thriving! Best purchase ever."</p>
            <span>— Sarah M.</span>
          </div>
          <div className="testimonial-card">
            <p>"Can't believe I grew my own grapefruit in Texas!"</p>
            <span>— Michael R.</span>
          </div>
          <div className="testimonial-card">
            <p>"The finger lime is a conversation starter at every dinner party."</p>
            <span>— Chef David K.</span>
          </div>
        </div>
        <div className="email-gate-header">
          {/* <span className="email-gate-label">You're almost there!</span> */}
          <h2 className="email-gate-title">
            {title}
          </h2>
          {description && <p className="email-gate-description">{description}</p>}
        </div>

        <ul className="unlock">
          <li>Your Citrus Personality</li>
          <li>Your top tree matches</li>
          <li className="gift">Your 99-page Care Guide, free as our gift</li>
          <li className="surprise">A little surprise</li>
        </ul>

        <form onSubmit={handleSubmit} className="email-gate-form">
          <div className="email-input-wrapper">
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="Email Address"
              className="email-input"
              autoFocus
            />
          </div>

          <label className="email-terms">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <span>
              I agree to receive personalized insights and accept the{' '}
              <a href="https://uscitrusnursery.com/policies/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a href="https://uscitrusnursery.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </span>
          </label>

          {error && (
            <div className="email-error">{error}</div>
          )}

          {helperText && (
            <p className="email-gate-helper">{helperText}</p>
          )}

          <div className="email-slide-navigation">
            <button
              type="submit"
              className="quiz-btn quiz-btn-primary email-submit-btn"
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? (
                <span className="loading-spinner" aria-label="Finding your results…" />
              ) : (
                buttonLabel ?? 'SHOW MY RESULTS'
              )}
            </button>
            <button
              type="button"
              className="quiz-btn quiz-btn-back"
              onClick={onBack}
              disabled={isSubmitting}
            >
              ← BACK
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailGate;
